/* eslint-disable no-console */
/* eslint-disable react/no-danger */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import { ReactSearchAutocomplete } from 'react-search-autocomplete';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import { select } from '@panel/commons/core/themev2';
import { Loader, useLoader } from '@panel/commons/lib/Loader';
import { USER_TYPE } from '@panel/commons/constants';
import { Modal, ModalBase as ModalContainer } from '@panel/commons';
import { useModal } from '@panel/commons/hooks';
import {
  BannerContainer,
  ErrorMessage,
  FormControl,
  InputWrapper,
  LoaderWrapper,
  SearchResult,
  StyledConfirm,
  StyledConfirmBody,
  StyledConfirmButton,
  StyledInputWrapper,
  StyledLabel,
  StyledWrapper,
  Submit,
  TextInput,
  Title
} from './styles';
import {
  getLeadsSubCompanies,
  getLeadsIntegrationState,
  addLeadsIntegration,
  listLeadsIntegrators,
  getCurrentLeadsIntegration,
  clearAddedLeadsIntegration,
  clearCurrentLeadsIntegrations,
  clearRemovedLeadsIntegration,
  removeLeadsIntegration
} from '../ducks';
import { IntegrationsVisualizer } from './leadsIntegrationsVisualizer';

const defaultFuzzySearchOptions = {
  shouldSort: true,
  threshold: 0.6,
  location: 0,
  distance: 800,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: ['name']
};

function formatResult(item) {
  return <SearchResult>{item.name}</SearchResult>;
}

const DEFAULT_BANNER_IDLE = {
  title: '',
  message: '',
  type: 'idle'
};

const formSchema = yup.object().shape({
  companyId: yup.number().when('$companies', (companies, schema) => {
    if (companies?.length === 0) {
      return schema.nullable().optional();
    }
    return schema.required();
  }),
  integratorId: yup.number().required(),
  companyCode: yup.string().when(['$integrators', 'integratorId'], (integrators, integratorId, schema) => {
    const obj = integrators.find(it => it.id === integratorId);
    if (obj?.code === true) {
      return schema.required();
    }
    return schema.nullable().optional();
  }),
  callbackURL: yup.string().when(['$integrators', 'integratorId'], (integrators, integratorId, schema) => {
    const obj = integrators.find(it => it.id === integratorId);
    if (obj?.enableCUI === true) {
      return schema.required();
    }
    return schema.nullable().optional();
  })
});

function LeadsIntegrationPanel({ user }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const lastIntegrator = useRef(undefined);
  const lastCompany = useRef(undefined);
  const candidate = useRef(undefined);
  const { toggleLoader, setToggleLoader } = useLoader();
  const [optionals, setOptionals] = useState({ code: false, url: false });
  const { loading, companies, integrators, activation, deactivation, companyIntegrations } = useSelector(
    getLeadsIntegrationState
  );
  const { handleSubmit, trigger, errors, control, getValues, setValue, register } = useForm({
    context: { companies: companies.data, integrators: integrators.data },
    resolver: yupResolver(formSchema),
    mode: 'onBlur',
    shouldFocusError: true
  });
  const [banner, setBanner] = useState(DEFAULT_BANNER_IDLE);
  const $observer = useWatch({ name: ['companyId', 'integratorId'], control });
  const { isShowing: toggleModal, toggleValue: setToggleModal } = useModal();

  useEffect(() => {
    setToggleLoader(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    register('companyId');
    register('integratorId');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deactivation.loading === false && (deactivation.data != null || deactivation.error != null)) {
      setToggleModal(false);
    }
    if (deactivation.data != null) {
      const { integratorName = '' } = deactivation.data;
      setBanner({
        title: 'Sucesso',
        message: t('panel.integrations.commons.errors.ic.deactivate.success', {
          integrationType: 'Leads',
          integratorName
        }),
        type: 'success'
      });
      if (companyIntegrations.loading === false) {
        dispatch(getCurrentLeadsIntegration(deactivation.data.companyId));
      }
    } else if (deactivation.error != null) {
      const { data } = deactivation.error;
      let defaultMessage = t('panel.integrations.commons.errors.ic.deactivate.errors.server', {
        integrationType: 'Leads'
      });
      if (data?.message && data?.message !== '...') {
        defaultMessage = t(data.message, {
          integratorName: candidate.current?.integratorName,
          integrationType: 'Leads'
        });
      }
      setBanner({
        title: 'Error',
        message: defaultMessage,
        type: 'error'
      });
    } else {
      setBanner(DEFAULT_BANNER_IDLE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deactivation]);

  useEffect(() => {
    if (activation.data != null) {
      setBanner({
        title: 'Sucesso',
        message: t('panel.integrations.leads.errors.ic.activate.success', {
          companyCode: activation.data?.companyCode
        }),
        type: 'success'
      });
      if (companyIntegrations.loading === false) {
        dispatch(getCurrentLeadsIntegration(activation.data.companyId));
      }
    } else if (activation.error != null) {
      const { data } = activation.error;
      let defaultMessage = t('panel.integrations.leads.errors.ic.activate.errors.server');
      if (data?.message && data?.message !== '...') {
        defaultMessage = t(data.message, { integrationType: 'Leads' });
      }
      setBanner({
        title: 'Error',
        message: defaultMessage,
        type: 'error'
      });
    } else {
      setBanner(DEFAULT_BANNER_IDLE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activation]);

  useEffect(() => {
    if (
      USER_TYPE.SUPERUSER === user?.userTypeId &&
      companyIntegrations.data === null &&
      companyIntegrations.loading === false
    ) {
      dispatch(getCurrentLeadsIntegration(user.publisherId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, companyIntegrations, integrators]);

  const onFocusCompany = () => {
    // Es Administrador
    if (companies.data.length === 0 && user?.userTypeId === USER_TYPE.ADMINISTRATOR && companies.loading === false) {
      dispatch(getLeadsSubCompanies());
    }
  };

  const handleSearchCompany = searchStr => {
    if (companies.data.length === 0 && user?.userTypeId === USER_TYPE.ADMINISTRATOR && companies.loading === false) {
      dispatch(getLeadsSubCompanies());
    }
    if (!!lastCompany.current && lastCompany.current !== searchStr) {
      setValue('companyId', undefined);
      dispatch(clearCurrentLeadsIntegrations());
    }
  };

  const handleSelectCompany = ({ id, name }) => {
    setValue('companyId', id);
    dispatch(getCurrentLeadsIntegration(id));
    if (activation.data || activation.error) {
      dispatch(clearAddedLeadsIntegration());
    }
    lastCompany.current = name;
  };

  const onClearCompany = () => {
    setValue('companyId', undefined);
    if (activation.data || activation.error) {
      dispatch(clearAddedLeadsIntegration());
    }
  };

  const onFocusIntegrators = () => {
    if (integrators.data.length === 0 && integrators.loading === false) {
      dispatch(listLeadsIntegrators());
    }
  };

  const handleSearchIntegrator = searchStr => {
    if (integrators.data.length === 0 && integrators.loading === false) {
      dispatch(listLeadsIntegrators());
    }
    if (!!lastIntegrator.current && lastIntegrator.current !== searchStr) {
      setValue('integratorId', undefined);
    }
  };

  const handleSelectIntegrator = ({ code = false, enableCUI = false, name, id }) => {
    setOptionals({ code, url: enableCUI });
    setValue('integratorId', id);
    lastIntegrator.current = name;
  };

  const onClearIntegrator = () => {
    setValue('integratorId', '');
  };

  const onSubmit = data => {
    dispatch(clearAddedLeadsIntegration());
    dispatch(addLeadsIntegration({ ...data }));
  };

  const onError = err => {
    if (process.env?.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(err);
      // eslint-disable-next-line no-console
      console.info(getValues());
    }
  };

  const handleDeactivateIntegrationConfirm = () => {
    if (typeof candidate.current === 'undefined') {
      return;
    }
    const { companyId, interfaceId } = candidate.current;
    if (typeof companyId !== 'undefined' && typeof interfaceId !== 'undefined' && deactivation.loading === false) {
      dispatch(removeLeadsIntegration(companyId, interfaceId));
    }
  };

  const handleCancelDeactivation = () => {
    setToggleModal(false);
    candidate.current = undefined;
  };

  const onClickDeactivateIntegration = args => {
    dispatch(clearRemovedLeadsIntegration());
    setToggleModal(true);
    candidate.current = args;
  };

  return (
    <div className='container'>
      {toggleLoader && (
        <LoaderWrapper>
          <Loader isUrbania={process.env.REACT_APP_BUILD_SITE === 'UBPE'} />
        </LoaderWrapper>
      )}
      <Title>{t('company:integration.leads.MainTitle')}</Title>
      {banner?.type !== 'idle' && (
        <BannerContainer type={banner?.type}>
          <h3>{banner?.title}</h3>
          <div dangerouslySetInnerHTML={{ __html: banner?.message }} />
        </BannerContainer>
      )}
      {companyIntegrations.data?.length > 0 && (
        <IntegrationsVisualizer
          title='Integrações ativas'
          headers={['Integrador', t('panel.integrations.commons.fields.companycode'), 'Desativar']}
          data={companyIntegrations.data}
          onDelete={onClickDeactivateIntegration}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit, onError)} autoComplete='false' noValidate>
        {user?.userTypeId === USER_TYPE.ADMINISTRATOR && (
          <FormControl className='column'>
            <StyledWrapper>
              <StyledInputWrapper className='search'>
                <ReactSearchAutocomplete
                  name='companies'
                  items={companies?.data}
                  showIcon={false}
                  showClear={false}
                  inputDebounce={200}
                  onFocus={onFocusCompany}
                  onClear={onClearCompany}
                  onSelect={handleSelectCompany}
                  onSearch={handleSearchCompany}
                  onBlur={() => trigger('companyId')}
                  styling={{
                    marginTop: '18px',
                    position: 'relative',
                    zIndex: 9,
                    fontSize: '14px',
                    lineHeight: '22px',
                    border: `1px solid ${select('midDarkGray')}`,
                    height: '48px',
                    boxShadow: '0px',
                    borderRadius: '8px',
                    fontFamily: 'Hind'
                  }}
                  className='search-input'
                  autoFocus
                  maxLength={100}
                  maxResults={8}
                  fuseOptions={defaultFuzzySearchOptions}
                  showNoResultsText='Sem resultados de pesquisa'
                  formatResult={formatResult}
                />
                <StyledLabel htmlFor='companies' hasContent={!!$observer.companyId} style={{ zIndex: '10' }}>
                  Sucursal
                </StyledLabel>
              </StyledInputWrapper>
              {errors.companyId && (
                <ErrorMessage>{t('panel.integrations.commons.validations.errors.required')}</ErrorMessage>
              )}
            </StyledWrapper>
          </FormControl>
        )}
        <FormControl>
          <StyledWrapper>
            <StyledInputWrapper className='search'>
              <ReactSearchAutocomplete
                name='search'
                items={integrators.data}
                showIcon={false}
                showClear={false}
                inputDebounce={200}
                onClear={onClearIntegrator}
                onSearch={handleSearchIntegrator}
                onFocus={onFocusIntegrators}
                onSelect={handleSelectIntegrator}
                onBlur={() => trigger('integratorId')}
                styling={{
                  marginTop: '18px',
                  position: 'relative',
                  zIndex: 7,
                  fontSize: '14px',
                  lineHeight: '22px',
                  border: `1px solid ${select('midDarkGray')}`,
                  height: '48px',
                  boxShadow: '0px',
                  borderRadius: '8px',
                  fontFamily: 'Hind'
                }}
                maxLength={100}
                maxResults={8}
                fuseOptions={defaultFuzzySearchOptions}
                className='search-input'
                autoFocus
                showNoResultsText='Sem resultados de pesquisa'
                formatResult={formatResult}
              />
              <StyledLabel htmlFor='search' hasContent={!!$observer.integratorId} style={{ zIndex: '8' }}>
                Integrador
              </StyledLabel>
            </StyledInputWrapper>
            {errors.integratorId && (
              <ErrorMessage>{t('panel.integrations.commons.validations.errors.required')}</ErrorMessage>
            )}
          </StyledWrapper>
          {optionals.code && (
            <StyledWrapper>
              <StyledInputWrapper>
                <TextInput type='text' defaultValue='' name='companyCode' required ref={register} />
                <StyledLabel htmlFor='companyCode'>{t('panel.integrations.commons.fields.companycode')}</StyledLabel>
              </StyledInputWrapper>
              {errors.companyCode && (
                <ErrorMessage>{t('panel.integrations.commons.validations.errors.required')}</ErrorMessage>
              )}
            </StyledWrapper>
          )}
        </FormControl>
        {optionals.url && (
          <FormControl>
            <StyledWrapper>
              <StyledInputWrapper>
                <TextInput type='text' defaultValue='' name='callbackURL' ref={register} required />
                <StyledLabel htmlFor='callbackURL'>URL CALLBACK</StyledLabel>
              </StyledInputWrapper>
              {errors.callbackURL && (
                <ErrorMessage>
                  {errors.callbackURL?.type === 'url'
                    ? t('panel.integrations.commons.validations.errors.url.invalid')
                    : t('panel.integrations.commons.validations.errors.required')}
                </ErrorMessage>
              )}
            </StyledWrapper>
          </FormControl>
        )}
        <FormControl>
          <InputWrapper>
            <Submit type='submit' disabled={loading}>
              Salvar
            </Submit>
          </InputWrapper>
        </FormControl>
      </form>
      {toggleModal && (
        <Modal>
          <ModalContainer
            isLoading={deactivation.loading}
            height='190px'
            openModal={toggleModal}
            onClose={() => setToggleModal(false)}
            withoutOutsideClose
            withoutCloseButton
          >
            <StyledConfirm>
              <StyledConfirmBody>
                <div className='confirm-text'>
                  {t('panel.integrations.commons.deactivate.confirm.message', { integrationType: 'Leads' })}
                </div>
                <div className='btn-container'>
                  <StyledConfirmButton onClick={handleCancelDeactivation} cancel>
                    {t('panel.integrations.commons.deactivate.confirm.btn.cancel')}
                  </StyledConfirmButton>
                  <StyledConfirmButton onClick={handleDeactivateIntegrationConfirm} ok>
                    {t('panel.integrations.commons.deactivate.confirm.btn.ok')}
                  </StyledConfirmButton>
                </div>
              </StyledConfirmBody>
            </StyledConfirm>
          </ModalContainer>
        </Modal>
      )}
    </div>
  );
}

export { LeadsIntegrationPanel };
